import { useEffect, useState } from "react";
import { Plus, Trash2, Eye, RefreshCw, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { DOC_STATUS, DOC_TYPES, MEALS, fdate, fmt, num, run } from "../lib/util";
import { Alert, Badge, Btn, Field, Input, ProductSelect, cellCls, Pending } from "../components/kit";
import { useSchool } from "./ctx";

const HEAD = {
  nf: [["supplier", "Fornecedor"], ["supplier_doc", "CNPJ/CPF"], ["number", "Número da nota"], ["series", "Série"], ["access_key", "Chave de acesso (44 dígitos)"], ["issue_date", "Data de emissão", "date"]],
  mapa_anterior: [["reference_month", "Mês de referência do mapa", "month"]],
  cardapio: [["reference_month", "Mês de referência", "month"]],
  contrato_af: [["number", "Número do contrato"], ["supplier", "Fornecedor, agricultor ou cooperativa"], ["supplier_doc", "CNPJ/CPF"], ["start_date", "Data inicial", "date"], ["end_date", "Data final", "date"]],
  ficha_tecnica: [],
};
const COLS = {
  nf: [["description", "Produto (nota)"], ["unit", "Un."], ["qty", "Qtd.", "number"], ["unit_price", "V. unit.", "number"], ["total", "V. total", "number"], ["product_id", "Produto do estoque", "product"], ["factor", "Fator p/ un. estoque", "number"]],
  mapa_anterior: [["description", "Gênero (mapa)"], ["unit", "Un."], ["final_balance", "Saldo final", "number"]],
  cardapio: [["date", "Data", "date"], ["meal", "Refeição", "meal"], ["preparation", "Preparação"]],
  contrato_af: [["description", "Produto (contrato)"], ["unit", "Un."], ["qty", "Qtd. contratada", "number"], ["unit_price", "V. unit.", "number"], ["product_id", "Produto do estoque", "product"], ["factor", "Fator p/ un. estoque", "number"]],
  ficha_tecnica: [["food", "Alimento"], ["group", "Grupo alimentar"], ["per_capita_g", "Per capita (g ou ml por aluno)", "number"], ["meal", "Refeição", "meal"], ["preparation", "Preparação / ficha"]],
};
const GROUP_COLS = [["group", "Grupo"], ["foods", "Alimentos que se substituem (separe por ;)"], ["rule", "Regra / equivalência"]];
const hasStock = (k) => k === "nf" || k === "contrato_af";

function Groups({ data, setData, editable }) {
  const rows = data.groups || [];
  const set = (i, k, v) => setData({ ...data, groups: rows.map((r, j) => (j === i ? { ...r, [k]: v || null } : r)) });
  return (
    <div className="space-y-2" data-testid="review-groups">
      <h4 className="font-heading font-bold text-[#0B192C] text-sm">Guia de substituições de grupos alimentares</h4>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm mapia-table">
          <thead><tr><th>#</th>{GROUP_COLS.map(([k, l]) => <th key={k}>{l}</th>)}{editable && <th />}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`review-group-${i}`}>
                <td className="text-xs text-slate-400">{i + 1}</td>
                {GROUP_COLS.map(([k]) => <td key={k}>{editable ? <input className={cellCls} value={r[k] || ""} onChange={(e) => set(i, k, e.target.value)} /> : r[k]}</td>)}
                {editable && <td><button onClick={() => setData({ ...data, groups: rows.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-red-500" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && <Btn variant="outline" data-testid="review-add-group" onClick={() => setData({ ...data, groups: [...rows, { group: null, foods: null, rule: null }] })}><Plus className="w-4 h-4" />Adicionar grupo</Btn>}
    </div>
  );
}
const kindOf = (t) => (t.startsWith("nf_") ? "nf" : t);

function Cell({ col, row, onChange, products, editable, skip }) {
  const [key, , type] = col;
  const v = row[key];
  const miss = !skip && (v == null || v === "");
  if (!editable) return <span>{type === "product" ? products.find((p) => p.id === v)?.name || "—" : type === "meal" ? MEALS[v] : type === "number" ? fmt(v) : v}</span>;
  if (type === "product")
    return <ProductSelect products={products} value={v} allowCreate createDefaults={{ name: row.description || "", unit: row.unit || "kg" }}
      onChange={(id) => onChange({ ...row, product_id: id, factor: row.factor ?? (COLS.mapa_anterior.includes(col) ? undefined : 1) })} />;
  if (type === "meal")
    return (
      <select className={`${cellCls} ${miss ? "border-amber-400 bg-amber-50" : ""}`} value={v || ""} onChange={(e) => onChange({ ...row, meal: e.target.value || null })}>
        <option value="">— pendente —</option>
        {Object.entries(MEALS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    );
  return (
    <input className={`${cellCls} ${miss ? "border-amber-400 bg-amber-50" : ""} ${type === "number" ? "text-right" : ""}`} type={type === "number" ? "number" : type || "text"} step="any"
      value={v ?? ""} placeholder={miss ? "pendente" : skip && key === "qty" ? "não comprado" : ""} onChange={(e) => onChange({ ...row, [key]: type === "number" ? num(e.target.value) : e.target.value || null })} />
  );
}

function Viewer({ id, type }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/s/documents/${id}/file`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [id]);
  if (!url) return <div className="text-sm text-slate-500 p-4">Carregando original…</div>;
  if (type.startsWith("image/")) return <img src={url} alt="Documento original" className="w-full rounded-lg border" />;
  if (type === "application/pdf") return <iframe title="original" src={url} className="w-full h-[70vh] rounded-lg border" />;
  return <a href={url} target="_blank" rel="noreferrer" className="text-[#FF6500] font-semibold">Abrir arquivo original</a>;
}

export default function DocReview({ id, onClose, onChanged }) {
  const { products, reloadProducts, month } = useSchool();
  const [doc, setDoc] = useState(null);
  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [reason, setReason] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    const r = await api.get(`/s/documents/${id}`);
    setDoc(r.data);
    setData(r.data.data || { items: [], entries: [] });
  };
  useEffect(() => {
    load().catch((e) => setErr(errMsg(e)));
    api.get("/s/contracts").then((r) => setContracts(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!doc || !data) return null;
  const kind = kindOf(doc.doc_type);
  const rowsKey = kind === "cardapio" ? "entries" : "items";
  const rows = data[rowsKey] || [];
  const editable = ["extraido", "erro", "duplicado"].includes(doc.status);
  const setRow = (i, r) => setData({ ...data, [rowsKey]: rows.map((x, j) => (j === i ? r : x)) });
  const save = async (quiet) => {
    setErr("");
    try {
      const r = await api.put(`/s/documents/${id}`, { data, version: doc.version });
      setDoc(r.data);
      setData(r.data.data);
      if (!quiet) run(async () => {}, "Alterações salvas");
      onChanged();
      return r.data;
    } catch (e) {
      setErr(errMsg(e));
      return null;
    }
  };
  const act = async (path, body) => {
    setErr("");
    try {
      const r = await api.post(`/s/documents/${id}/${path}`, body);
      onChanged();
      reloadProducts();
      if (path === "reextract" || path === "confirm" || path === "fill-ficha") {
        if (path === "confirm") run(async () => {}, "Documento confirmado — estoque e controles recalculados");
        if (path === "fill-ficha") run(async () => {}, "Completando refeição, preparação e regras pelo cardápio… abra novamente em instantes");
        return onClose();
      }
      setDoc(r.data);
      setData(r.data.data);
      run(async () => {}, { confirm: "Documento confirmado — estoque e controles recalculados", reopen: "Documento reaberto para edição", discard: "Documento descartado" }[path]);
    } catch (e) {
      setErr(errMsg(e));
      load();
    }
  };
  const confirm = async () => {
    const im = (data?.issue_date || "").slice(0, 7);
    if (doc.doc_type.startsWith("nf_") && im && im !== month && !window.confirm(`A data de emissão desta nota (${fdate(data.issue_date)}) é de ${im.slice(5)}/${im.slice(0, 4)}, fora do mês em trabalho (${month.slice(5)}/${month.slice(0, 4)}).\nEla vai entrar no estoque de ${im.slice(5)}/${im.slice(0, 4)}. Confirmar mesmo assim?`)) return;
    const d = await save(true);
    if (d) act("confirm", { version: d.version, not_duplicate_reason: reason || null });
  };
  const blank = Object.fromEntries(COLS[kind].map(([k]) => [k, null]));
  const stockQty = (r) => (r.qty != null && r.factor ? r.qty * r.factor : null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[1200px] w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="doc-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3">
            Conferência · {DOC_TYPES[doc.doc_type].short} <Badge tone={DOC_STATUS[doc.status][1]}>{DOC_STATUS[doc.status][0]}</Badge>
            <span className="text-xs font-normal text-slate-500">{doc.filename} · leitura: {data.source === "xml" ? "XML NF-e" : data.source === "ia" ? "IA (conferir)" : "manual"}</span>
          </DialogTitle>
        </DialogHeader>
        {doc.error && <Alert tone="red">{doc.error}</Alert>}
        {doc.doc_type.startsWith("nf_") && data?.issue_date && data.issue_date.slice(0, 7) !== month && (
          <Alert title="Nota fora do mês em trabalho" testid="review-other-month">
            Emissão em {fdate(data.issue_date)}; o mês em trabalho é {month.slice(5)}/{month.slice(0, 4)}. A nota entra no estoque do mês da emissão. Confira a data antes de confirmar.
          </Alert>
        )}
        {doc.suspicion?.length > 0 && doc.status !== "confirmado" && (
          <Alert title="Possível duplicidade — conferência necessária" testid="suspicion-alert">
            {doc.suspicion.map((s) => <div key={s.document_id}>{s.reason}: “{s.filename}” ({DOC_STATUS[s.status]?.[0]})</div>)}
            <div className="mt-1 text-xs">Não foi possível descartar a duplicidade automaticamente. Se tiver certeza de que é outro documento, justifique abaixo antes de confirmar.</div>
            {editable && <Input data-testid="not-duplicate-reason-input" className="mt-2" placeholder="Justificativa (ex.: nota complementar com série distinta)" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Alert>
        )}
        <div className={`grid gap-5 ${show ? "lg:grid-cols-[1fr_420px]" : ""}`}>
          <div className="space-y-4 min-w-0">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {HEAD[kind].map(([k, l, t]) => (
                <Field key={k} label={l} pending={data[k] == null || data[k] === ""}>
                  <Input data-testid={`review-${k}-input`} disabled={!editable} type={t || "text"} value={data[k] ?? ""} onChange={(e) => setData({ ...data, [k]: e.target.value || null })} />
                </Field>
              ))}
              {doc.doc_type === "nf_af" && (
                <Field label="Contrato vinculado" pending={!data.contract_id}>
                  <select data-testid="review-contract-select" disabled={!editable} className={cellCls + " py-2"} value={data.contract_id || ""} onChange={(e) => setData({ ...data, contract_id: e.target.value || null })}>
                    <option value="">— sem contrato —</option>
                    {contracts.map((c) => <option key={c.id} value={c.id}>{c.number} · {c.supplier}</option>)}
                  </select>
                </Field>
              )}
            </div>
            <div className="overflow-x-auto rounded-xl border">
              <table className="w-full text-sm mapia-table">
                <thead><tr><th>#</th>{COLS[kind].map(([k, l]) => <th key={k}>{l}</th>)}{hasStock(kind) && <th>Qtd. no estoque</th>}{editable && <th />}</tr></thead>
                <tbody>
                  {rows.map((r, i) => { const skip = kind === "contrato_af" && !(r.qty > 0); return (
                    <tr key={i} data-testid={`review-row-${i}`} className={skip ? "opacity-60" : ""}>
                      <td className="text-xs text-slate-400">{i + 1}{r.suggested && <div title="Vínculo sugerido pelo histórico" className="text-[10px] text-blue-600">sug.</div>}</td>
                      {COLS[kind].map((c) => <td key={c[0]}><Cell col={c} row={r} products={products} editable={editable} skip={skip} onChange={(nr) => setRow(i, { ...nr, suggested: false })} /></td>)}
                      {hasStock(kind) && <td className="num">{skip ? <span data-testid={`review-row-${i}-not-bought`} className="text-xs font-semibold text-slate-500">não comprado</span> : stockQty(r) == null ? <Pending /> : `${fmt(stockQty(r))} ${products.find((p) => p.id === r.product_id)?.unit || ""}`}</td>}
                      {editable && <td><button onClick={() => setData({ ...data, [rowsKey]: rows.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-red-500" /></button></td>}
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
            {editable && <Btn variant="outline" data-testid="review-add-row" onClick={() => setData({ ...data, [rowsKey]: [...rows, { ...blank }] })}><Plus className="w-4 h-4" />Adicionar linha</Btn>}
            {kind === "ficha_tecnica" && <Groups data={data} setData={setData} editable={editable} />}
            {hasStock(kind) && <p className="text-xs text-slate-500">O fator converte a unidade do documento para a unidade de estoque (ex.: caixa com 12 un. → fator 12). A quantidade é preservada sem arredondamento.</p>}
          </div>
          {show && <div className="lg:sticky lg:top-0 self-start"><Viewer id={id} type={doc.content_type} /></div>}
        </div>
        {err && <Alert tone="red" testid="review-error">{err}</Alert>}
        <div className="flex flex-wrap gap-2 justify-between pt-2 border-t">
          <div className="flex gap-2">
            <Btn variant="outline" data-testid="review-toggle-original" onClick={() => setShow(!show)}><Eye className="w-4 h-4" />{show ? "Ocultar original" : "Ver original"}</Btn>
            {kind === "ficha_tecnica" && ["erro", "extraido"].includes(doc.status) && <Btn variant="outline" data-testid="review-fill-ficha" onClick={() => act("fill-ficha")}><Wand2 className="w-4 h-4" />Completar pelo cardápio</Btn>}
            {["erro", "extraido"].includes(doc.status) && doc.content_type !== "application/xml" && <Btn variant="outline" data-testid="review-reextract" onClick={() => act("reextract")}><RefreshCw className="w-4 h-4" />Ler novamente com IA</Btn>}
          </div>
          <div className="flex gap-2">
            {doc.status !== "confirmado" && doc.status !== "descartado" && <Btn variant="danger" data-testid="review-discard" onClick={() => window.confirm("Descartar este documento?") && act("discard", { version: doc.version })}>Descartar</Btn>}
            {doc.status === "confirmado" && <Btn variant="outline" data-testid="review-reopen" onClick={() => act("reopen", { version: doc.version })}>Reabrir para editar</Btn>}
            {editable && <Btn variant="navy" data-testid="review-save" onClick={() => save()}>Salvar rascunho</Btn>}
            {editable && doc.status !== "duplicado" && <Btn data-testid="review-confirm" onClick={confirm}>Confirmar conferência</Btn>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
